<template>
  <div>
    <v-container fluid class="px-0">
      <v-row>
        <v-col cols="12" align="right">
          <ToggleButton
            class="hide-on-print"
            :state="collapseButtonState"
            color="primary"
            :statesLabel="collapseButtonLabels"
            @click="(state) => this.collapseButtonState = state === 1 ? 2 : 1 "
          ></ToggleButton>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-layout column>
            <v-data-table
              dense
              :headers="headers"
              :items="clientBillingSummaries"
              item-key="clientId"
              :sort-by="['clientName']"
              :sort-desc="[false]"
              class="elevation-1"
              :expanded="expanded"
              hide-default-footer
              show-expand
              :items-per-page="-1"
            >
            <template v-slot:item.totalBillableHours="{ item }">
              {{ item.totalBillableHours | formatDecimal }}
            </template>
            <template v-slot:item.totalBillAmount="{ item }">
              {{ item.totalBillAmount | currency }}
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td class="py-5" :colspan="headers.length">
                <v-data-table
                    dense
                    :headers="subHeaders"
                    :items="item.billingSummaries"
                    class="pb-3 px-5"
                >
                  <template v-slot:item.billRate="{ item }">
                    {{ item.billRate | currency }} {{ item.billRateTypeId | getBillRateTypeLabel }}
                  </template>
                  <template v-slot:item.stBill="{ item }">
                    {{ item.stBill | currency }}
                  </template>
                  <template v-slot:item.stHours="{ item }">
                    {{ item.stHours | formatDecimal }}
                  </template>
                  <template v-slot:item.otBill="{ item }">
                    {{ item.otBill | currency }}
                  </template>
                  <template v-slot:item.otHours="{ item }">
                    {{ item.otHours | formatDecimal }}
                  </template>
                  <template v-slot:item.totalBillableHours="{ item }">
                    {{ item.totalBillableHours | formatDecimal }}
                  </template>
                  <template v-slot:item.totalBill="{ item }">
                    <span :class="{
                      'blue-highlighted' : item.totalBillEdited === true,
                      'yellow-highlighted': item.needsToBeProrated === true
                    }">{{ item.totalBill | currency }}</span>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      class="mx-1 my-1 hide-on-print"
                      color="primary"
                      outlined
                      @click="handleClickEditTotalBill(item)"
                    >
                      <v-icon color="primary">edit</v-icon>
                    </v-btn>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-1 my-1 hide-on-print"
                          color="primary"
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click.prevent="handleClickViewTimeCard($event, item)"
                        >
                          <v-icon color="primary">schedule</v-icon>
                        </v-btn>
                      </template>
                      <span>Go to time card</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-1 my-1 hide-on-print"
                          color="primary"
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click="handleClickViewOneOffFees(item)"
                        >
                          <v-icon color="primary">price_check</v-icon>
                        </v-btn>
                      </template>
                      <span>View one off fees</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </td>
            </template>
            </v-data-table>
          </v-layout>
        </v-col>
      </v-row>
    </v-container>
    <OneOffFeesRecordListModal
      :assignmentId="modalAssignmentId"
      :userName="modalUserName"
      :clientName="modalClientName"
      :showDateRangePicker="false"
      :filterDateBegin="filterDateBegin"
      :filterDateEnd="filterDateEnd"
      @closeModal="$emit('OneOffFeeListModalClose')"
      ref="OneOffFeesRecordListModal"
    >
    </OneOffFeesRecordListModal>
    <EditTotalBillModal
      ref="editTotalBillModal"
      :totalBillEditedId="modalTotalBillEditedId"
      :dateBegin="dateBegin"
      :dateEnd="dateEnd"
      :assignmentId="modalAssignmentId"
      :consultant="modalUserName"
      :client="modalClientName"
      :totalBill="modalTotalBill"
      @editedTotalBill="editedTotalBillHandler"
    >
    </EditTotalBillModal>
  </div>
</template>

<script>

import { timeCardReportRoute } from '@/lib/constants/routes'
import { OneOffFeesRecordListModal } from '@/components/oneOffFeesRecords'
import { mapFields } from '@/vueHelpers/vuex'
import EditTotalBillModal from './EditTotalBillModal'
import { ToggleButton } from '@/components/buttons/'

export default {
  components: {
    ToggleButton,
    OneOffFeesRecordListModal,
    EditTotalBillModal
  },
  props: [
    'clientBillingSummaries'
  ],
  data () {
    return {
      headers: [
        { text: 'Client', value: 'clientName', sortable: false, cellClass: 'py-3' },
        { text: 'Total Billable Hours', value: 'totalBillableHours', align: 'center', sortable: false },
        { text: 'Total Bill Amount', value: 'totalBillAmount', align: 'center', sortable: false }
      ],
      modalAssignmentId: null,
      modalTotalBillEditedId: null,
      modalUserName: null,
      modalClientName: null,
      modalTotalBill: null,
      filterDateBegin: null,
      filterDateEnd: null,
      collapseButtonLabels: {
        1: 'Contract All',
        2: 'Expand All'
      },
      collapseButtonState: 1 // 1 - Expanded, 2 - Collapsed
    }
  },
  computed: {
    ...mapFields('invoicingReportFilters', [
      'dateBegin',
      'dateEnd',
      'showDetails'
    ]),
    subHeaders () {
      let headers = []
      if (this.showDetails) {
        headers = [
          { text: 'Consultant', value: 'userFullName', width: '150px' },
          { text: 'Bill Rate', value: 'billRate', width: '150px' },
          { text: 'ST', value: 'stHours', width: '100px' },
          { text: 'ST Bill', value: 'stBill', width: '100px' },
          { text: 'OT', value: 'otHours', width: '100px' },
          { text: 'OT Bill', value: 'otBill', width: '100px' },
          { text: 'Total Billable Hours', value: 'totalBillableHours', width: '170px' },
          { text: 'Total Bill', value: 'totalBill', width: '100px', class: 'px-2', cellClass: 'px-2' },
          { text: 'Actions', value: 'actions', align: 'center', sortable: false, class: 'hide-on-print', cellClass: 'hide-on-print px-0', width: '250px' }
        ]
      } else {
        headers = [
          { text: 'Consultant', value: 'userFullName', width: '20%' },
          { text: 'Bill Rate', value: 'billRate', width: '18%' },
          { text: 'Total Billable Hours', value: 'totalBillableHours', width: '20%' },
          { text: 'Total Bill', value: 'totalBill', width: '15%', class: 'px-2', cellClass: 'px-2' },
          { text: 'Actions', value: 'actions', align: 'center', sortable: false, class: 'hide-on-print', cellClass: 'hide-on-print px-0', width: '27%' }
        ]
      }
      return headers
    },
    expanded: {
      get () {
        if (this.collapseButtonState === 1) {
          return this.clientBillingSummaries || []
        } else {
          return []
        }
      }
    }
  },
  methods: {
    handleClickViewTimeCard (event, item) {
      let clientId = item.clientId
      let userId = item.userId
      let assignmentId = item.assignmentId
      let startDate = this.dateBegin
      let endDate = this.dateEnd
      const routeData = this.$router.resolve({
        name: timeCardReportRoute.name,
        query: { startDate: startDate, endDate: endDate, clientId: clientId, userId: userId, assignmentId: assignmentId }
      })
      if (event.ctrlKey === true) {
        window.open(routeData.href, '_blank')
      } else {
        window.open(routeData.href, '_self')
      }
    },
    handleClickViewOneOffFees (item) {
      this.modalAssignmentId = item.assignmentId
      this.modalUserName = item.userFullName
      this.modalClientName = item.clientName
      this.filterDateBegin = item.invoicingPeriodDateBegin
      this.filterDateEnd = item.invoicingPeriodDateEnd
      this.$refs.OneOffFeesRecordListModal.showModal()
    },
    handleClickEditTotalBill (item) {
      this.modalTotalBillEditedId = item.totalBillEditedId
      this.modalAssignmentId = item.assignmentId
      this.modalClientName = item.clientName
      this.modalUserName = item.userFullName
      this.modalTotalBill = item.totalBill
      this.$refs.editTotalBillModal.showModal()
    },
    editedTotalBillHandler () {
      this.$emit('editedTotalBill')
    }
  }
}
</script>
